import { X } from "lucide-react";
import { useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useT } from "@/lib/i18n";
import { useGrammarRule } from "@/lib/grammar";

type RulePopupProps = {
  // The rule's slug (or id — the backend accepts both). Null = closed.
  slugOrId: string | null;
  onClose: () => void;
};

/**
 * Full-screen modal showing a grammar rule's full body within a
 * scenario session. Replaces the previous `<Link to="/grammar/...">`
 * affordance that took the user out of the session (and broke when
 * scenario rule slugs were passed to a route expecting an id).
 *
 * No SM-2 buttons — this is a *reading* surface, not a study card.
 * The user closes the popup and continues the session unchanged; the
 * mini-check after the RULE step is still where actual recall is
 * tested.
 *
 * Closes on backdrop click, Esc, or the close-X. Mounted at the page
 * level via `slugOrId` prop so the parent controls open/close.
 */
export function RulePopup({ slugOrId, onClose }: RulePopupProps) {
  const t = useT();
  // Fetch only when open. `useGrammarRule` gates on a truthy arg
  // already, so a null `slugOrId` just disables the query.
  const rule = useGrammarRule(slugOrId ?? undefined);

  useEffect(() => {
    if (!slugOrId) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [slugOrId, onClose]);

  if (!slugOrId) return null;

  return (
    <>
      {/* Backdrop — same dim level as the LockedExplainSheet for
          consistency across modals. */}
      <button type="button" onClick={onClose} aria-label="close" className="fixed inset-0 z-40 bg-black/45" />
      {/* Sheet — fills most of the viewport so the markdown body has
          room without being claustrophobic. Slightly inset from the
          top for the safe-area / nav clearance; bottom-flush with a
          large rounded corner so it reads as a modal, not a side
          panel. */}
      <div className="fixed inset-x-0 bottom-0 top-10 z-50 mx-auto flex max-w-[640px] flex-col overflow-hidden rounded-t-[20px] border-t border-border bg-surface shadow-[0_-8px_30px_rgba(0,0,0,0.18)]">
        {/* Drag handle + close-X header. Title doesn't go here because
            the rule body usually starts with a `# Title` h1 anyway —
            doubling would feel redundant. */}
        <div className="relative shrink-0 px-5 pt-3.5 pb-2">
          <div className="mx-auto mb-2.5 h-1 w-9 rounded-full" style={{ background: "var(--surface-3)" }} />
          <button
            type="button"
            onClick={onClose}
            aria-label="close"
            className="absolute right-3 top-3 rounded-md p-1.5 text-text-3 hover:bg-[color:var(--surface-2)]"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* Scroll area for the body. Tags strip + markdown content. */}
        <div className="flex-1 overflow-y-auto px-5 pb-4">
          {rule.isLoading && (
            <div className="flex items-center justify-center py-12 text-sm text-text-3">{t("common.loading")}</div>
          )}
          {rule.isError && (
            <div className="py-12 text-center text-sm text-[color:var(--again)]">
              {rule.error instanceof Error ? rule.error.message : t("common.failed_to_load")}
            </div>
          )}
          {rule.data && (
            <div className="md">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{rule.data.body}</ReactMarkdown>
            </div>
          )}
        </div>

        {/* Footer — single close CTA. Returns the user to the session,
            no progress side-effects (this was a reading break). */}
        <div
          className="shrink-0 border-t border-border bg-surface px-5 py-3"
          style={{
            paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-[var(--r-md)] py-3 text-[15px] font-semibold text-white transition-opacity hover:opacity-90"
            style={{ background: "var(--scen-solid)" }}
          >
            {t("skills.rule_popup_close")}
          </button>
        </div>
      </div>
    </>
  );
}
